import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import usePatientManualRegistrationLatamFormValidationScheme from '../../../Validation/PatientManualRegistrationLatamFormValidationScheme';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import InputField from '../../../../../Components/Form/components/InputField';
import { useAppContext } from '../../../../../Services/AppContext';
import FormControl from '../../../../../Components/Form/FormControl';
import TermItemRadioList from '../../../../Term/Components/TermItemRadioList';
import PhoneNumberField from '../../../../../Components/PhoneNumber/PhoneNumberField';
import PatientRegistrationHealthPlanSelect from '../Utils/PatientRegistrationHealthPlanSelect';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import { Moment } from 'moment';
import BirthDatePickerField from '../../../../Base/Components/Inputs/BirthDatePicker/BirthDatePickerFiled';
import Text from '../../../../../Components/Base/Text/Text';
// @ts-ignore
import styles from '../PatientManualRegistration.module.scss';
import FormContainer from '../../../../../Components/Form/FormContainer';
import usePatientRegistration from '../../../Hooks/usePatientRegistration';

export interface PatientManualRegistrationLatamFormValues {
	salutationBefore?: string;
	firstName: string;
	middleName?: string;
	fatherLastName: string;
	motherLastName?: string;
	lastName?: string;
	birthDate: Moment;
	gender: string;
	email: string;
	phoneNumber?: string;
	healthPlanCode?: SelectValue;
	healthPlanWorkplaceMember?: {
		id?: string;
		value?: string;
		label: string;
		item: any;
	};
	legalRepresentative?: {
		firstName: string;
		lastName: string;
		email: string;
		phoneNumber?: string;
	};
}

interface Props {
	onSuccess: (
		values: PatientManualRegistrationLatamFormValues,
		withHealthPlan: boolean,
	) => void;
}

const defaultValues: any = {};

const PatientManualRegistrationLatamForm: React.FC<Props> = ({ onSuccess }) => {
	const { globalTemplate } = useAppContext();
	const [isChild, setIsChild] = useState(false);
	const [hasHealthPlans, setHasHealthPlans] = useState(false);
	const { validationSchema } =
		usePatientManualRegistrationLatamFormValidationScheme(
			isChild,
			globalTemplate === 'peru',
			hasHealthPlans,
		);

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(validationSchema),
	});

	usePatientRegistration({ formMethods, setIsChild });

	const healthPlanCodeValue = formMethods.watch('healthPlanCode')?.value;

	const handleSuccess = (values: any) => {
		onSuccess(values as PatientManualRegistrationLatamFormValues, false);
	};

	const handleSuccessWithPlan = (values: any) => {
		onSuccess(values as PatientManualRegistrationLatamFormValues, true);
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<div className='mb-3'>
				<InputField
					name='salutationBefore'
					label='diabetes.patientManualRegistration.title'
					charactersLimit={50}
					showCharCounter={false}
				/>
			</div>
			<div className='mb-3'>
				<InputField
					name='firstName'
					label='diabetes.patientManualRegistration.firstName'
					isRequired
				/>
			</div>
			<div className='mb-3'>
				<InputField
					name='middleName'
					label='diabetes.patientManualRegistration.secondName'
				/>
			</div>
			<div className='mb-3'>
				<InputField
					name='fatherLastName'
					label='diabetes.patientManualRegistration.fathersName'
					isRequired
				/>
			</div>
			<div className='mb-3'>
				<InputField
					name='motherLastName'
					label='diabetes.patientManualRegistration.mothersName'
					isRequired={globalTemplate === 'peru'}
				/>
			</div>
			<div className='mb-3'>
				<InputField
					name='lastName'
					label='diabetes.patientManualRegistration.marriageName'
				/>
			</div>
			<div className='mb-3'>
				<BirthDatePickerField
					name='birthDate'
					label='base.shared.dateOfBirth'
					isRequired
				/>
			</div>
			<div className='mb-3'>
				<FormControl name='gender' label='base.shared.gender' isRequired>
					<TermItemRadioList termType='gender' inLine={true} />
				</FormControl>
			</div>
			{isChild && (
				<>
					<Text
						className={styles.formText}
						tx='base.shared.legalRepresentative'
						firstUpper
					/>
					<FormContainer name={'legalRepresentative'}>
						<div className='row mb-3'>
							<div className='col-lg-6 col-12'>
								<InputField
									name='firstName'
									label='diabetes.patientManualRegistration.firstName'
									isRequired
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField
									name='lastName'
									label='diabetes.patientManualRegistration.fathersName'
									isRequired
								/>
							</div>
						</div>
						<div className='row'>
							<div className='col-lg-6 col-12'>
								<PhoneNumberField
									name='phoneNumber'
									label='base.shared.phone'
								/>
							</div>
							<div className='col-lg-6 col-12'>
								<InputField
									name='email'
									label='base.shared.email'
									isRequired
									isWrapped
								/>
							</div>
						</div>
					</FormContainer>
				</>
			)}
			{!isChild && (
				<>
					<div className='mb-3'>
						<InputField name='email' label='base.shared.email' isRequired />
					</div>
					<div className='mb-3'>
						<PhoneNumberField
							name='phoneNumber'
							label='diabetes.patientManualRegistration.mobilePhone'
						/>
					</div>
				</>
			)}
			<div className='mb-3'>
				<PatientRegistrationHealthPlanSelect
					formMethods={formMethods}
					setHasHealthPlans={setHasHealthPlans}
				/>
			</div>
			<div className='v2-forms__gray-background footer mt-3'>
				<div
					className='d-flex flex-row align-items-center'
					style={{ gap: '10px' }}>
					<div>
						<SubmitButton
							onSubmit={handleSuccess}
							title='base.registerThePatient'
						/>
					</div>
					{healthPlanCodeValue && healthPlanCodeValue !== 'withoutPlan' && (
						<div>
							<SubmitButton
								onSubmit={handleSuccessWithPlan}
								title='component.patientManualRegistrationComponent.registerAndSetupHealthPlan'
							/>
						</div>
					)}
				</div>
			</div>
		</FormWrapper>
	);
};

export default PatientManualRegistrationLatamForm;
